/**
 * Empfehlungen für den DigiLernFlow 6-Wochen-Pfad.
 *
 * Verfügbare Boards: Kanban-Board | Lernlandkarte | Leitner-Box
 * Verfügbare Kartentypen: Lernkarte | Bildkarte | Promptkarte |
 *                         Interaktives Arbeitsblatt | PDF-Dokument
 *
 * Zielgruppe: Abiturienten – Wiederholung & Festigung.
 * Zweck: Profilspezifische Empfehlung für Schüler + Begleiter.
 */

const recommendations = {
  // ══════════════════════════════════════════════════════════════
  // NAVIGATOR (structure)
  // ══════════════════════════════════════════════════════════════
  structure_systematic: {
    boards: ['Kanban-Board', 'Lernlandkarte'],
    cardTypes: ['Lernkarte', 'Interaktives Arbeitsblatt', 'PDF-Dokument'],
    tools: ['KI-Prompts', 'ganze KI-Pfade'],
    strategicNote: 'Das Kanban-Board ist dein natürliches Zuhause – du brauchst Sichtbarkeit über den Status jeder Aufgabe.',
  },
  structure_visual: {
    boards: ['Lernlandkarte', 'Kanban-Board'],
    cardTypes: ['Bildkarte', 'Lernkarte', 'Interaktives Arbeitsblatt'],
    tools: ['Video', 'GeoGebra'],
    strategicNote: 'Bildkarten + strukturierte Lernlandkarte = dein optimales Duo.',
  },
  structure_interactive: {
    boards: ['Kanban-Board', 'Lernlandkarte'],
    cardTypes: ['Promptkarte', 'Interaktives Arbeitsblatt', 'Lernkarte'],
    tools: ['KI-Prompts', 'GeoGebra', 'JS-Rechner'],
    strategicNote: 'Promptkarten strukturieren deine KI-Dialoge – das verhindert das Abschweifen.',
  },
  structure_repetitive: {
    boards: ['Leitner-Box', 'Kanban-Board'],
    cardTypes: ['Lernkarte', 'Interaktives Arbeitsblatt', 'PDF-Dokument'],
    tools: ['ganze KI-Pfade', 'KI-Prompts'],
    strategicNote: 'Die Leitner-Box ist dein stärkstes Werkzeug – du brauchst nur den systematischen Rahmen darum.',
  },

  // ══════════════════════════════════════════════════════════════
  // SPRINTER (speed)
  // ══════════════════════════════════════════════════════════════
  speed_systematic: {
    boards: ['Kanban-Board', 'Leitner-Box'],
    cardTypes: ['Lernkarte', 'Interaktives Arbeitsblatt', 'PDF-Dokument'],
    tools: ['KI-Prompts', 'JS-Rechner'],
    strategicNote: 'Lernkarten als Stopp-Schilder: Du rechnest, hältst kurz an, checkst.',
  },
  speed_visual: {
    boards: ['Kanban-Board', 'Lernlandkarte'],
    cardTypes: ['Bildkarte', 'Interaktives Arbeitsblatt'],
    tools: ['Video', 'GeoGebra'],
    strategicNote: 'Bildkarten passen zu deinem Tempo – kurzer visueller Impuls, dann sofort handeln.',
  },
  speed_interactive: {
    boards: ['Kanban-Board', 'Lernlandkarte'],
    cardTypes: ['Promptkarte', 'Interaktives Arbeitsblatt', 'Lernkarte'],
    tools: ['KI-Prompts', 'GeoGebra', 'JS-Rechner'],
    strategicNote: 'Promptkarten als Echtzeit-Korrektor: ideal für dein Tempo.',
  },
  speed_repetitive: {
    boards: ['Leitner-Box', 'Kanban-Board'],
    cardTypes: ['Lernkarte', 'Interaktives Arbeitsblatt'],
    tools: ['KI-Prompts', 'ganze KI-Pfade'],
    strategicNote: 'Tägliche Kurzquizzes aus der Leitner-Box trainieren Präzision unter Zeitdruck.',
  },

  // ══════════════════════════════════════════════════════════════
  // EXPLORER (intuition)
  // ══════════════════════════════════════════════════════════════
  intuition_visual: {
    boards: ['Lernlandkarte', 'Leitner-Box'],
    cardTypes: ['Bildkarte', 'Interaktives Arbeitsblatt'],
    tools: ['GeoGebra', 'JS-Animationen', 'Video'],
    strategicNote: 'Bildkarten sind dein Kernmedium – Skizze vorne, Formel hinten.',
  },
  intuition_interactive: {
    boards: ['Lernlandkarte', 'Kanban-Board'],
    cardTypes: ['Promptkarte', 'Interaktives Arbeitsblatt', 'Bildkarte'],
    tools: ['GeoGebra', 'JS-Animationen', 'KI-Prompts'],
    strategicNote: 'Promptkarten übersetzen deine Intuition in mathematische Sprache.',
  },
  intuition_systematic: {
    boards: ['Lernlandkarte', 'Kanban-Board'],
    cardTypes: ['Lernkarte', 'Bildkarte', 'PDF-Dokument'],
    tools: ['GeoGebra', 'JS-Animationen'],
    strategicNote: 'Lernkarten als Checkliste geben deiner Intuition einen verlässlichen Rahmen.',
  },
  intuition_repetitive: {
    boards: ['Leitner-Box', 'Lernlandkarte'],
    cardTypes: ['Bildkarte', 'Interaktives Arbeitsblatt', 'Lernkarte'],
    tools: ['Video', 'JS-Animationen'],
    strategicNote: 'Bildkarten in der Leitner-Box sind für dich das wirksamste Wiederholungsformat.',
  },

  // ══════════════════════════════════════════════════════════════
  // ARCHITEKT (model)
  // ══════════════════════════════════════════════════════════════
  model_systematic: {
    boards: ['Lernlandkarte', 'Leitner-Box'],
    cardTypes: ['Lernkarte', 'PDF-Dokument'],
    tools: ['ganze KI-Pfade', 'KI-Prompts'],
    strategicNote: 'Du baust vollständige Wissensgebäude – deine Stärke.',
  },
  model_visual: {
    boards: ['Lernlandkarte', 'Leitner-Box'],
    cardTypes: ['Bildkarte', 'Interaktives Arbeitsblatt'],
    tools: ['JS-Animationen', 'GeoGebra', 'Video'],
    strategicNote: 'Animierte Beweise + Bildkarten = dein ideales Duo.',
  },
  model_interactive: {
    boards: ['Lernlandkarte', 'Kanban-Board'],
    cardTypes: ['Promptkarte', 'Interaktives Arbeitsblatt', 'Lernkarte'],
    tools: ['KI-Prompts', 'GeoGebra', 'JS-Rechner'],
    strategicNote: 'Promptkarten im Sokrates-Modus sind für dich das produktivste Lernformat.',
  },
  model_repetitive: {
    boards: ['Leitner-Box', 'Lernlandkarte'],
    cardTypes: ['Lernkarte', 'Promptkarte'],
    tools: ['KI-Prompts', 'ganze KI-Pfade'],
    strategicNote: 'Präzision in der Sprache ist für dich Pflicht und Freude.',
  },

  // ══════════════════════════════════════════════════════════════
  // GUARDIAN (analysis)
  // ══════════════════════════════════════════════════════════════
  analysis_systematic: {
    boards: ['Kanban-Board', 'Leitner-Box'],
    cardTypes: ['Lernkarte', 'Interaktives Arbeitsblatt'],
    tools: ['KI-Prompts', 'ganze KI-Pfade'],
    strategicNote: 'Dein Perfektionismus ist dein stärkster Kritiker – und dein schlechtester Starter.',
  },
  analysis_visual: {
    boards: ['Lernlandkarte', 'Kanban-Board'],
    cardTypes: ['Bildkarte', 'Interaktives Arbeitsblatt'],
    tools: ['Video', 'GeoGebra'],
    strategicNote: 'Bildkarten bauen Vertrauen auf – sie zeigen, dass du es kannst, bevor du anfängst.',
  },
  analysis_interactive: {
    boards: ['Kanban-Board', 'Lernlandkarte'],
    cardTypes: ['Promptkarte', 'Interaktives Arbeitsblatt', 'Lernkarte'],
    tools: ['KI-Prompts', 'GeoGebra', 'JS-Animationen'],
    strategicNote: 'Promptkarten bewerten nicht – das ist für dich der entscheidende Unterschied.',
  },
  analysis_repetitive: {
    boards: ['Leitner-Box', 'Kanban-Board'],
    cardTypes: ['Lernkarte', 'Promptkarte'],
    tools: ['KI-Prompts', 'ganze KI-Pfade'],
    strategicNote: 'Wiederholung bekannter Inhalte gibt dir die Sicherheit, die du brauchst, um Neues zu wagen.',
  },
};

const defaultRecommendation = {
  boards: ['Kanban-Board', 'Lernlandkarte'],
  cardTypes: ['Lernkarte', 'Interaktives Arbeitsblatt', 'PDF-Dokument'],
  tools: ['Video', 'GeoGebra', 'KI-Prompts'],
  strategicNote: 'Dein individuelles Profil wird gemeinsam mit deinem Begleiter in der ersten Session präzisiert.',
};

export function getDashboardRecommendation(modeId, toolId) {
  const key = `${modeId}_${toolId}`;
  return recommendations[key] || defaultRecommendation;
}